import React, { FC } from 'react';
import { NCFileBatchEventsProps } from '@v2/pages/common/components/NCFileBatchEvents';
import { scale as initialScale } from '@components/frame';
import { View } from 'react-native';
import { isTabletOrWeb, machineStatusColorName } from '@helpers';
import DotIndicator from '@components/wallboard/components/dotIndicator';
import { DOT_SIZE } from '@v2/pages/common/components/NCFileBatchEvents/constants';
import { EventsPerspective, EventTimelineData } from '@v2/pages/common/components/NCFileBatchEvents/types';
import { TimeCategory } from '@interfaces/base';
import { TILE_COLORS } from './TimeTiles/constants';
import { useManageEventBarChartInternalLogic } from '@v2/pages/common/components/NCFileBatchEvents/viewmodels/useManageEventBarChartInternalLogic';

export interface EventBarChartProps extends Pick<NCFileBatchEventsProps, 'C' | 'scale' | 'eventType'>, Partial<EventTimelineData> {
  eventsPerspective: EventsPerspective | null;
  isFirstElement?: boolean;
}

const EventBarChart: FC<EventBarChartProps> = ({
  C,
  eventType,
  eventsPerspective,
  activeTimeInSec = 0,
  changeTimeInSec = 0,
  setupTimeInSec = 0,
  maxTimeInSec = 0,
  idealTimeInSec = 0,
  paidTimeInSec = 0,
  actualTimeInSec = 0,
  scheduledTimeInSec = 0,
  showDots = false,
  scale = initialScale,
  isFirstElement = false,
}) => {
  const { shouldRender, barChartWidth, setBarChartWidth, barSegments, dotIndicatorPercentages, dotRefs, shouldRegisterPositions } =
    useManageEventBarChartInternalLogic({
    eventType,
    eventsPerspective,
    activeTimeInSec,
    changeTimeInSec,
    setupTimeInSec,
    maxTimeInSec,
    idealTimeInSec,
    paidTimeInSec,
    actualTimeInSec,
    scheduledTimeInSec,
    isFirstElement,
  });

  if (!shouldRender) return null;

  const borderRadiusConfig = { borderTopRightRadius: scale(3), borderBottomRightRadius: scale(3) };

  return (
    <View style={{ width: '100%' }}>
      <View style={[{ position: 'relative' }, isTabletOrWeb && { transform: [{ translateY: -scale(2) }] }]}>
        <View
          onLayout={(event) => setBarChartWidth(event.nativeEvent.layout.width - scale(DOT_SIZE / 2))} // to prevent cutting off dots by timeline component when time percentage value is 100%
          style={{ flexDirection: 'row', height: scale(6), borderRadius: scale(3), overflow: 'hidden', backgroundColor: C('Background/Light/Chart') }}
        >
          {barSegments.map(
            ({ status, percentage, hasBorderRadius }) =>
              percentage > 0 && (
                <View
                  key={status}
                  style={[
                    {
                      width: `${percentage}%`,
                      height: '100%',
                      backgroundColor: C(machineStatusColorName[status]),
                    },
                    hasBorderRadius && borderRadiusConfig,
                  ]}
                />
              )
          )}
        </View>

        {/* Dot indicators */}
        {showDots && barChartWidth > 0 && (
          <View style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, flexDirection: 'row', alignItems: 'center' }}>
            {Object.values(TimeCategory).map((category) => {
              const percentage = dotIndicatorPercentages[category];
              if (percentage <= 0) return null;

              return (
                <View
                  key={category}
                  ref={shouldRegisterPositions ? dotRefs[category] : undefined}
                  style={{
                    position: 'absolute',
                    left: Math.min(Math.max(0, (percentage / 100) * barChartWidth - scale(DOT_SIZE / 2)), barChartWidth - scale(DOT_SIZE)),
                  }}
                >
                  <DotIndicator
                    size={DOT_SIZE}
                    color={TILE_COLORS[category]}
                    hasOutline={true}
                    outlineColor="Background/Light/Primary"
                    outlineWidth={2}
                    scale={scale}
                    C={C}
                    style={{}}
                  />
                </View>
              );
            })}
          </View>
        )}
      </View>
    </View>
  );
};

export default EventBarChart;
